import {
  alpha, Box, Button, List, ListItem, ListSubheader, styled,
} from '@mui/material';
import SpaceDashboardTwoToneIcon from '@mui/icons-material/SpaceDashboardTwoTone';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { NavLink as RouterLink } from 'react-router';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { useTranslation } from 'react-i18next';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BusinessIcon from '@mui/icons-material/Business';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { ReactElement } from 'react';
import { useDashboardLayout } from '@/contexts/DashboardLayoutContext';
import FRONTEND_ROUTES, { FRONTEND_ROUTES_KEYS } from '@/consts/frontendRoutes';
import {
  canAccessView, canPerformAction, PERMISSION_ACTION, UI_VIEW,
} from '@/lib/authZ';

interface SidebarMenuItem {
  toRoute: string;
  title: string;
  onClick: () => void;
  startIcon: ReactElement;
  allowedToAccess: boolean;
}

interface SidebarMenuSection {
  id: string;
  title?: string;
  items: SidebarMenuItem[];
}

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.white[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
      background: transparent;
      white-space: normal;

      ${theme.breakpoints.down('sm')} {
        padding: ${theme.spacing(0, 2)};
        font-size: ${theme.typography.pxToRem(11)};
      }
    }
`,
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.white[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          align-items: center;
          padding: ${theme.spacing(1.2, 3)};
          text-align: left;
          white-space: normal;
          line-height: 1.35;

          ${theme.breakpoints.down('sm')} {
            padding: ${theme.spacing(1, 2)};
            font-size: ${theme.typography.pxToRem(13)};
          }

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.white[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
            margin-top: 0;
            flex-shrink: 0;
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.white[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.white[100], 0.06)};
            color: ${theme.colors.alpha.white[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.white[100]};
            }
          }

          &:focus-visible {
            outline: 2px solid #f9a467;
            outline-offset: 2px;
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.white[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
    'transform',
    'opacity',
  ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`,
);

const SidebarMenu = () => {
  const { closeSidebar } = useDashboardLayout();

  const canAccessOrganizationUsersTable = canAccessView(UI_VIEW.organizationUsersTable);
  const canAccessIndependentUsersTable = canAccessView(UI_VIEW.independentUsersTable);
  const canAccessOrganizationsTable = canAccessView(UI_VIEW.organizationsTable);
  const canAccessOrganizationLicensePool = canPerformAction(
    PERMISSION_ACTION.viewOrganizationLicensePool,
  );
  const canAccessOrganizationSettingsPage = canAccessView(UI_VIEW.organizationSettingsPage);

  const { t } = useTranslation('common');

  const sidebarMenuSections: SidebarMenuSection[] = [
    {
      id: 'main',
      items: [
        {
          toRoute: FRONTEND_ROUTES.DASHBOARD,
          title: t('SIDEBAR_TITLES.DASHBOARD'),
          onClick: closeSidebar,
          startIcon: <SpaceDashboardTwoToneIcon />,
          allowedToAccess: true,
        },
        {
          toRoute: FRONTEND_ROUTES.USER_SESSIONS.replace(FRONTEND_ROUTES_KEYS.SESSION_ID, ''),
          title: t('SIDEBAR_TITLES.MY_SESSIONS'),
          onClick: closeSidebar,
          startIcon: <VideoCameraFrontIcon />,
          allowedToAccess: true,
        },
        {
          toRoute: FRONTEND_ROUTES.MY_MEDIA.replace(FRONTEND_ROUTES_KEYS.MEDIA_UUID, ''),
          title: t('SIDEBAR_TITLES.MY_MEDIA'),
          onClick: closeSidebar,
          startIcon: <PermMediaIcon />,
          allowedToAccess: true,
        },
      ],
    },
    {
      id: 'organization',
      title: t('SIDEBAR_SECTIONS.MY_ORGANIZATION'),
      items: [
        {
          toRoute: FRONTEND_ROUTES.SELF_USER_MANAGEMENT,
          title: t('SIDEBAR_TITLES.SELF_USER_MANAGEMENT'),
          onClick: closeSidebar,
          startIcon: <ManageAccountsIcon />,
          allowedToAccess: canAccessOrganizationUsersTable,
        },
        {
          toRoute: FRONTEND_ROUTES.LICENSE_MANAGEMENT,
          title: t('SIDEBAR_TITLES.LICENSE_MANAGEMENT'),
          onClick: closeSidebar,
          startIcon: <ConfirmationNumberIcon />,
          allowedToAccess: canAccessOrganizationLicensePool,
        },
        {
          toRoute: FRONTEND_ROUTES.ORGANIZATION_SETTINGS,
          title: t('SIDEBAR_TITLES.ORGANIZATION_SETTINGS'),
          onClick: closeSidebar,
          startIcon: <BusinessIcon />,
          allowedToAccess: canAccessOrganizationSettingsPage,
        },
      ],
    },
    {
      id: 'platform',
      title: t('SIDEBAR_SECTIONS.PLATFORM_ADMINISTRATION'),
      items: [
        {
          toRoute: FRONTEND_ROUTES.INDEPENDENT_USER_MANAGEMENT,
          title: t('SIDEBAR_TITLES.INDEPENDENT_USER_MANAGEMENT'),
          onClick: closeSidebar,
          startIcon: <AdminPanelSettingsIcon />,
          allowedToAccess: canAccessIndependentUsersTable,
        },
        {
          toRoute: FRONTEND_ROUTES.ORGANIZATION_MANAGEMENT,
          title: t('SIDEBAR_TITLES.ORGANIZATION_MANAGEMENT'),
          onClick: closeSidebar,
          startIcon: <BusinessCenterIcon />,
          allowedToAccess: canAccessOrganizationsTable,
        },
      ],
    },
  ];

  const visibleSections = sidebarMenuSections
    .map((section) => ({
      ...section,
      visibleItems: section.items.filter((item) => item.allowedToAccess),
    }))
    .filter((section) => section.visibleItems.length > 0);

  return (
    <MenuWrapper sx={{ pb: 1 }}>
      <List component="div">
        <SubMenuWrapper>
          {visibleSections.map((section, sectionIndex) => (
            <List
              key={section.id}
              component="div"
              subheader={section.title ? (
                <ListSubheader
                  disableSticky
                  sx={{ mt: sectionIndex > 0 ? 1.5 : 0 }}
                >
                  {section.title}
                </ListSubheader>
              ) : undefined}
            >
              {section.visibleItems.map((sideMenuItem) => (
                <ListItem component="div" key={sideMenuItem.title}>
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={sideMenuItem.onClick}
                    to={sideMenuItem.toRoute}
                    startIcon={sideMenuItem.startIcon}
                  >
                    {sideMenuItem.title}
                  </Button>
                </ListItem>
              ))}
            </List>
          ))}
        </SubMenuWrapper>
      </List>
    </MenuWrapper>
  );
};

export default SidebarMenu;
